<!-- Responsibility: Manage user roles and permissions.
Scope: View-only card for admin role grants state. -->
<admin-permission-card>
  <section class="admin-card">
    <header>
      <h2>Permission Manager</h2>
      <button type="button" class="action-btn subtle" onclick={ () => openModal() } disabled={ !props.isSupremeAdmin }>GRANT ROLE</button>
    </header>
    <p class="admin-copy">{ props.isSupremeAdmin ? 'Supreme admin can grant roles.' : 'View-only unless you are supreme admin.' } `{ props.supremeEmail }` is always treated as supreme admin.</p>
    
    <ul class="admin-roster">
      <li each={ grant in props.roleGrants } data-supreme={ grant.role === 'supreme' ? 'true' : 'false' }>
        <div class="admin-roster-copy">
          <strong>{ props.principalName(grant) }</strong>
          <span>{ props.principalHandle(grant) }</span>
        </div>
        <div class="admin-roster-meta">
          <span class="provider-chip">{ props.formatRole(grant.role) }</span>
          <small>{ grant.updatedAtMs ? `Updated ${props.formatTime(grant.updatedAtMs)}` : (grant.role === 'supreme' ? 'Reserved identity' : 'Imported grant') }</small>
        </div>
        <div class="admin-actions compact">
          <button type="button" class="action-btn subtle" onclick={ () => editGrant(grant) }>EDIT</button>
          <button if={ props.isSupremeAdmin && grant.role !== 'supreme' } type="button" class="action-btn subtle" onclick={ () => props.onQuickSetRole(grant, 'admin') }>ADMIN</button>
          <button if={ props.isSupremeAdmin && grant.role !== 'supreme' } type="button" class="action-btn subtle" onclick={ () => props.onQuickSetRole(grant, 'moderator') }>MOD</button>
          <button if={ props.isSupremeAdmin && grant.role !== 'supreme' } type="button" class="action-btn subtle" onclick={ () => props.onQuickSetRole(grant, 'creator') }>CREATOR</button>
          <button if={ props.isSupremeAdmin && grant.role !== 'supreme' } type="button" class="action-btn subtle" onclick={ () => props.onQuickSetRole(grant, 'normal') }>NORMAL</button>
        </div>
      </li>
    </ul>

    <dialog ref="roleModal" class="admin-form-modal">
      <div class="admin-modal-content">
        <header>
          <h3>Manage Role Grant</h3>
          <button type="button" class="modal-close-hit" onclick={ closeModal }>✕</button>
        </header>
        <div ref="roleHost" class="admin-form-host"></div>
        <div class="admin-actions">
          <button type="button" class="action-btn primary" onclick={ saveGrant }>SAVE ROLE</button>
          <button type="button" class="action-btn" onclick={ resetForm }>RESET</button>
        </div>
      </div>
    </dialog>
  </section>

  <script>
    import { AutoForm, collectValues, populateFields } from 'field-assist'
    import { adminService } from '../../../services/admin-service.ts'
    import {
      buildAdminRoleInitialValues,
      buildAdminRoleFormMeta,
      buildAdminRoleSubmission,
    } from '../../helpers/admin-role-form.ts'

    export default {
      editGrant(grant) {
        this.renderRoleForm(grant)
        this.openModal()
      },

      renderRoleForm(grant = {}) {
        const host = this.$('[ref="roleHost"]')
        if (!(host instanceof HTMLElement)) return
        host.replaceChildren()
        const initialValues = buildAdminRoleInitialValues(grant)
        const autoForm = new AutoForm(buildAdminRoleFormMeta())
        autoForm.render(initialValues, host)
        populateFields(host, initialValues, 'name')
      },

      openModal() {
        const modal = this.$('[ref="roleModal"]')
        if (modal instanceof HTMLDialogElement) modal.showModal()
      },

      closeModal() {
        const modal = this.$('[ref="roleModal"]')
        if (modal instanceof HTMLDialogElement) modal.close()
      },

      async saveGrant() {
        const host = this.$('[ref="roleHost"]')
        if (!(host instanceof HTMLElement)) return
        const values = collectValues(host, undefined, 'name')
        const submission = buildAdminRoleSubmission(values)
        await adminService.saveRoleGrant(submission)
        this.closeModal()
      },

      resetForm() {
        this.renderRoleForm()
      }
    }
  </script>

  <style>
    .admin-card {
      display: grid;
      gap: var(--space-sm);
      border: 1px solid color-mix(in srgb, var(--border) 46%, transparent);
      border-radius: 1rem;
      padding: var(--space-md);
      background: var(--surface-raised);
      box-shadow: var(--shadow-elev-2);
    }

    header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: var(--space-sm);
    }

    h2 {
      margin: 0;
      color: var(--text-prose);
    }

    .admin-copy {
      color: var(--text-muted);
      font-size: var(--type-sm);
      margin: 0;
    }

    .admin-roster {
      display: grid;
      gap: var(--space-sm);
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .admin-roster li {
      display: grid;
      gap: var(--space-xs);
      padding: var(--space-sm);
      border: 1px solid color-mix(in srgb, var(--border) 28%, transparent);
      border-radius: 0.5rem;
      background: color-mix(in srgb, var(--bg) 40%, transparent);
    }

    .admin-roster-copy {
      display: flex;
      flex-direction: column;
    }

    .admin-roster-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: var(--type-xs);
      color: var(--text-muted);
    }

    .admin-actions.compact {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
      margin-top: var(--space-xs);
    }

    .provider-chip {
      background: var(--surface-subtle);
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-size: 0.7rem;
      text-transform: uppercase;
    }

    .admin-form-modal {
      background: var(--surface-raised);
      border: 1px solid var(--border);
      border-radius: 1rem;
      color: var(--text-prose);
      padding: 0;
      max-width: 31.25rem;
      width: 90vw;
    }

    .admin-modal-content {
      padding: var(--space-lg);
      display: grid;
      gap: var(--space-md);
    }

    .modal-close-hit {
      background: none;
      border: none;
      color: var(--text-muted);
      cursor: pointer;
      font-size: 1.5rem;
    }
  </style>
</admin-permission-card>
